import React, { useEffect, useRef, useState } from 'react';
import cytoscape, { Core, EventObject } from 'cytoscape';
// @ts-ignore
import dagre from 'cytoscape-dagre';
import { MultiColumnVizProps, useChartTheme } from './types';
import { dsVar, ink } from '../../theme/tokens';
import { useThemeMode, useThemeTokens } from '../../theme/ThemeModeProvider';
import {
    ALL_LAYERS,
    EDGE,
    Layers,
    collapseAttributes,
    filterLayers,
    layersPresent,
    normaliseGraph,
    resolveStyleMapping,
    toCyElements,
} from './networkGraph/networkGraphModel';

// Register dagre layout in case it's specifically requested, though 'concentric' is built-in
cytoscape.use(dagre);

interface NetworkGraphProps extends MultiColumnVizProps {
    /** Entity ids to bring forward (with the links between them); everything else is dimmed. */
    highlightEntityIds?: string[];
}

interface SelectedElement {
    type: 'node' | 'edge';
    id: string;
    label?: string;
    name?: string;
    kind?: string;
    description?: string;
    properties: Record<string, any>;
}

const LAYER_LABELS: Record<keyof Layers, string> = {
    money: 'Money flows',
    attributes: 'Shared attributes',
    sameParty: 'Same party',
};

const chipStyle = (active: boolean): React.CSSProperties => ({
    fontSize: 11,
    fontFamily: '"Inter", sans-serif',
    padding: '4px 10px',
    borderRadius: 999,
    cursor: 'pointer',
    border: `1px solid ${active ? dsVar('accent-fg') : dsVar('line')}`,
    background: active ? 'rgba(255,222,89,0.1)' : 'transparent',
    color: active ? dsVar('accent-fg') : dsVar('fg-2'),
});

const NetworkGraphVisualization: React.FC<NetworkGraphProps> = ({ data, metadata, highlightEntityIds }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const cyRef = useRef<Core | null>(null);
    // Cytoscape paints to canvas: it needs resolved hex, not CSS variables.
    const theme = useChartTheme();
    const tokens = useThemeTokens();
    const { resolved } = useThemeMode();

    // Interactivity state
    const [selectedElement, setSelectedElement] = useState<SelectedElement | null>(null);
    const [layers, setLayers] = useState<Layers>(ALL_LAYERS);
    const [collapsed, setCollapsed] = useState(false);

    // Memoize parsed data to prevent unnecessary effect triggers. Normalising also
    // drops edges to missing nodes, which Cytoscape would otherwise throw on.
    const graph = React.useMemo(() => {
        const { graph: g, dropped } = normaliseGraph(data);
        if (dropped) console.warn(`NetworkGraphVisualization: dropped ${dropped} edge(s) whose endpoint is not in the graph`);
        return g;
    }, [data]);

    const present = React.useMemo(() => layersPresent(graph), [graph]);
    const hasDemographics = present.attributes || present.sameParty;

    const visibleGraph = React.useMemo(() => {
        const g = collapsed ? collapseAttributes(graph) : graph;
        return filterLayers(g, layers);
    }, [graph, layers, collapsed]);

    const { styleMapping, layoutConfigs } = metadata || {};

    const { nodeTypeColors: nodeColors, edgeTypeColors: edgeColors } = React.useMemo(
        () => resolveStyleMapping(styleMapping, resolved),
        [styleMapping, resolved]
    );

    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Cytoscape resizes its canvas with the container but keeps its pan, so a wider
    // container (a widened drawer, the detail panel closing) leaves the graph off to
    // one side. Panning by half the change keeps whatever was centred, centred.
    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === 'undefined') return;
        let last: { width: number; height: number } | null = null;
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            if (last && cyRef.current) {
                cyRef.current.panBy({ x: (width - last.width) / 2, y: (height - last.height) / 2 });
            }
            last = { width, height };
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!containerRef.current || (!visibleGraph.nodes.length && !visibleGraph.edges.length)) return;

        const edgeColor = (ele: any) => edgeColors[ele.data('type')] || theme.text.muted;

        const cy = cytoscape({
            container: containerRef.current,
            elements: toCyElements(visibleGraph),
            style: [
                {
                    selector: 'node',
                    style: {
                        'background-color': (ele) => {
                            const lbl = ele.data('label');
                            return nodeColors[lbl] || theme.text.accent;
                        },
                        'label': 'data(name)',
                        'color': tokens['fg-body'],
                        'font-size': '12px',
                        'font-family': 'Inter, sans-serif',
                        'text-valign': 'bottom',
                        'text-halign': 'center',
                        'text-margin-y': 6,
                        'border-width': 2,
                        'border-color': theme.border.default,
                        'width': 45,
                        'height': 45,
                        'text-outline-color': theme.bg.deep,
                        'text-outline-width': 2,
                        'transition-property': 'width, height, border-width',
                        'transition-duration': 200
                    }
                },
                {
                    // Attribute nodes: smaller, diamond, labelled with the masked value
                    selector: 'node[kind = "attribute"]',
                    style: {
                        'shape': 'diamond',
                        'width': 26,
                        'height': 26,
                        'font-size': '10px',
                        'border-width': 1,
                    }
                },
                {
                    // Weak (non identity-grade) attributes: address, IP, device
                    selector: 'node[kind = "attribute"][strength = "weak"]',
                    style: { 'opacity': 0.55 }
                },
                {
                    selector: 'node:selected',
                    style: {
                        'border-width': 6,
                        'border-color': tokens.fg
                    }
                },
                {
                    selector: 'edge',
                    style: {
                        'width': 2,
                        'line-color': edgeColor,
                        'target-arrow-color': edgeColor,
                        'target-arrow-shape': 'triangle',
                        'curve-style': 'bezier',
                        // Edge label is hidden by default; shown only on hover via .edge-hover class
                        'label': '',
                        'font-size': '10px',
                        'font-family': 'Inter, sans-serif',
                        'color': theme.text.muted,
                        'text-rotation': 'autorotate',
                        'text-margin-y': -8,
                        'text-background-color': theme.bg.deep,
                        'text-background-opacity': 0.8,
                        'text-background-padding': '2px',
                        'transition-property': 'width, line-color, target-arrow-color',
                        'transition-duration': 200
                    }
                },
                {
                    // Evidence, not flow: thin, light, no arrowhead
                    selector: `edge[type = "${EDGE.HAS_ATTRIBUTE}"]`,
                    style: { 'width': 1, 'target-arrow-shape': 'none', 'opacity': 0.7 }
                },
                {
                    // Undirected; always labelled with the match score
                    selector: `edge[type = "${EDGE.POSSIBLE_SAME_PARTY}"]`,
                    style: { 'line-style': 'dashed', 'target-arrow-shape': 'none', 'width': 2.5, 'label': 'data(score)' }
                },
                {
                    selector: `edge[type = "${EDGE.SHARED_ATTRIBUTE}"]`,
                    style: { 'line-style': 'dotted', 'target-arrow-shape': 'none' }
                },
                {
                    // Reveal the edge description only when the edge is hovered
                    selector: 'edge.edge-hover',
                    style: {
                        'label': 'data(description)',
                        'width': 3,
                    }
                },
                {
                    selector: 'edge:selected',
                    style: {
                        'width': 4,
                        'line-color': tokens.fg,
                        'target-arrow-color': tokens.fg
                    }
                },
                {
                    selector: '.dimmed',
                    style: { 'opacity': 0.12 }
                },
                {
                    selector: 'node.highlighted',
                    style: { 'border-width': 4, 'border-color': theme.text.accent }
                },
                {
                    selector: 'edge.highlighted',
                    style: { 'width': 3 }
                }
            ],
            zoomingEnabled: true,
            userZoomingEnabled: true,
            panningEnabled: true,
            userPanningEnabled: true,
        });

        // ── Layout ────────────────────────────────────────────────────────────
        // IMPORTANT: animate MUST be false.
        // When animate:true, Cytoscape schedules a requestAnimationFrame loop.
        // If the component is unmounted (or the user switches items) while that
        // loop is running, the last frame fires *after* cy.destroy() and tries
        // to call renderer.notify(), crashing because the renderer is already
        // null. With animate:false, positions are applied synchronously and no
        // RAF is ever queued, completely eliminating the race condition.
        const layoutName = (() => {
            const requested = (layoutConfigs?.preferredLayout || 'concentric').toLowerCase();
            const available = ['null', 'random', 'preset', 'grid', 'circle', 'concentric', 'breadthfirst', 'cose', 'dagre'];
            if (available.includes(requested)) return requested;
            return requested.includes('cose') ? 'cose' : 'concentric';
        })();

        const layoutInstance = cy.layout({
            name: layoutName,
            spacingFactor: layoutConfigs?.nodeSpacing ? layoutConfigs.nodeSpacing / 50 : undefined,
            animate: false, // Never animate — see comment above
            padding: 40,
        } as any);

        layoutInstance.run();

        // Interactivity handling
        cy.on('tap', 'node', (evt: EventObject) => {
            const node = evt.target;
            setSelectedElement({
                type: 'node',
                id: node.data('id'),
                label: node.data('label'),
                name: node.data('name'),
                kind: node.data('kind'),
                properties: node.data('props') || {}
            });
        });

        cy.on('tap', 'edge', (evt: EventObject) => {
            const edge = evt.target;
            setSelectedElement({
                type: 'edge',
                id: edge.data('id'),
                label: edge.data('type'),
                description: edge.data('description'),
                properties: edge.data('props') || {}
            });
        });

        cy.on('tap', (evt: EventObject) => {
            if (evt.target === cy) {
                cy.elements().unselect();
                setSelectedElement(null);
            }
        });

        // Show edge description on hover; hide it when mouse leaves
        cy.on('mouseover', 'edge', (evt: EventObject) => {
            evt.target.addClass('edge-hover');
        });
        cy.on('mouseout', 'edge', (evt: EventObject) => {
            evt.target.removeClass('edge-hover');
        });

        cyRef.current = cy;

        return () => {
            // Stop layout first (no-op if already finished since animate:false)
            try { layoutInstance.stop(); } catch (_) { /* ignore */ }
            if (cyRef.current) {
                try {
                    cyRef.current.stop();
                    cyRef.current.destroy();
                } catch (_) { /* ignore */ }
                cyRef.current = null;
            }
        };
    }, [visibleGraph, nodeColors, edgeColors, layoutConfigs, theme, tokens]);

    // Highlight a pattern's entities, the links between them and the attributes they
    // share; dim the rest. Re-applied whenever the graph is rebuilt.
    useEffect(() => {
        const cy = cyRef.current;
        if (!cy) return;
        cy.elements().removeClass('dimmed highlighted');
        if (!highlightEntityIds?.length) return;

        const ids = new Set(highlightEntityIds.map(String));
        const entities = cy.nodes().filter(n => ids.has(n.id()));
        if (entities.empty()) return;
        const sharedAttributes = entities.neighborhood('node[kind = "attribute"]')
            .filter(a => a.neighborhood('node').intersection(entities).length >= 2);
        const members = entities.union(sharedAttributes);
        const focus = members.union(members.edgesWith(members));

        cy.elements().not(focus).addClass('dimmed');
        focus.addClass('highlighted');
    }, [highlightEntityIds, visibleGraph, nodeColors, edgeColors, layoutConfigs, theme, tokens]);

    const selectedColor = selectedElement
        ? (selectedElement.type === 'node'
            ? (nodeColors[selectedElement.label || ''] || theme.text.accent)
            : (edgeColors[selectedElement.label || ''] || theme.text.muted))
        : undefined;

    const detailEntries = selectedElement
        ? Object.entries(selectedElement.properties).filter(([key, val]) =>
            val !== null && val !== undefined && val !== ''
            // Shown in the heading / masked-value block instead
            && !(selectedElement.kind === 'attribute' && (key === 'display' || key === 'name')))
        : [];

    return (
        <div style={{ background: theme.bg.card, border: `1px solid ${theme.border.default}`, borderRadius: 14, padding: '24px 28px', marginBottom: 32 }}>
            {hasDemographics && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    {(Object.keys(LAYER_LABELS) as (keyof Layers)[]).filter(k => present[k]).map(k => (
                        <button
                            key={k}
                            type="button"
                            aria-pressed={layers[k]}
                            onClick={() => setLayers(l => ({ ...l, [k]: !l[k] }))}
                            style={chipStyle(layers[k])}
                        >
                            {LAYER_LABELS[k]}
                        </button>
                    ))}
                    {present.attributeNodes && (
                        <button
                            type="button"
                            aria-pressed={collapsed}
                            onClick={() => setCollapsed(c => !c)}
                            title="Replace each entity → attribute ← entity path with one dotted link"
                            style={{ ...chipStyle(collapsed), marginLeft: 8 }}
                        >
                            Collapse attributes
                        </button>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginLeft: 'auto', fontSize: 11, color: theme.text.muted, fontFamily: '"Inter", sans-serif' }}>
                        {present.money && <LegendLine color={edgeColors[EDGE.TRANSACTED_WITH]} dash="solid" label="Money flow" />}
                        {present.attributes && <LegendLine color={edgeColors[EDGE.SHARED_ATTRIBUTE]} dash="dotted" label="Shares attribute" />}
                        {present.sameParty && <LegendLine color={edgeColors[EDGE.POSSIBLE_SAME_PARTY]} dash="dashed" label="Possible same party" />}
                    </div>
                </div>
            )}
            <div style={{ display: 'flex', gap: 24, position: 'relative' }}>
                <div style={{ flex: 1, minWidth: 0, height: Math.max(500, window.innerHeight * 0.5), position: 'relative', borderRadius: 8, overflow: 'hidden', background: theme.bg.deep, border: `1px solid ${theme.border.default}` }}>
                    <div ref={containerRef} style={{ width: '100%', height: '100%' }} />

                    {/* Control Hints */}
                    <div style={{ position: 'absolute', bottom: 12, left: 16, fontSize: 11, color: theme.text.muted, pointerEvents: 'none' }}>
                        Scroll to zoom • Drag background to pan • Click elements for details
                    </div>
                </div>

                {/* Sided Overlay Panel */}
                <div style={{
                    width: selectedElement ? 300 : 0,
                    flexShrink: 0,
                    transition: 'width 0.2s ease',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {selectedElement && (
                        <div style={{ background: dsVar('raised'), border: `1px solid ${theme.border.default}`, borderRadius: 10, padding: 16, height: Math.max(500, window.innerHeight * 0.5), overflowY: 'auto' }} className="custom-scrollbar">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: theme.text.muted, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{
                                        width: 12, height: 12, borderRadius: selectedElement.type === 'node' ? '50%' : '0%', flexShrink: 0,
                                        backgroundColor: selectedColor
                                    }} />
                                    {selectedElement.type === 'node'
                                        ? (selectedElement.kind === 'attribute' ? 'Attribute Details' : 'Node Details')
                                        : 'Edge Details'}
                                </div>
                                <button onClick={() => {
                                    if(cyRef.current) cyRef.current.elements().unselect();
                                    setSelectedElement(null);
                                }}
                                style={{ background: 'transparent', border: 'none', color: theme.text.muted, cursor: 'pointer', padding: '4px' }}>✕</button>
                            </div>

                            <h4 style={{ color: theme.text.accent, fontSize: 16, fontWeight: 600, marginBottom: 6, wordBreak: 'break-word', borderBottom: `1px solid ${theme.border.default}`, paddingBottom: 12 }}>
                                {selectedElement.type === 'node'
                                    ? (selectedElement.kind === 'attribute' ? selectedElement.label : selectedElement.name) || selectedElement.id
                                    : selectedElement.description || selectedElement.label || selectedElement.id}
                            </h4>

                            {selectedElement.type === 'edge' && selectedElement.label === EDGE.POSSIBLE_SAME_PARTY && (
                                <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: ink('#f97316'), background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.35)', borderRadius: 4, padding: '2px 8px', marginTop: 6 }}>
                                    Duplicate identity? — KYC review
                                </span>
                            )}

                            {selectedElement.kind === 'attribute' && (
                                <div style={{ marginTop: 12 }}>
                                    <div style={{ fontSize: 11, color: theme.text.muted, marginBottom: 4, fontFamily: '"Inter", sans-serif' }}>MASKED VALUE</div>
                                    <div style={{ fontSize: 13, color: dsVar('fg-body'), wordBreak: 'break-word', fontFamily: '"JetBrains Mono", monospace' }}>
                                        {String(selectedElement.properties.display ?? selectedElement.properties.name ?? '')}
                                    </div>
                                    <div style={{ fontSize: 11, color: theme.text.muted, marginTop: 6, fontFamily: '"Inter", sans-serif' }}>
                                        The full value is held on the customer record.
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                                {detailEntries.map(([key, val]) => (
                                    <div key={key}>
                                        <div style={{ fontSize: 11, color: theme.text.muted, marginBottom: 4, fontFamily: '"Inter", sans-serif' }}>
                                            {key.replace(/_/g, ' ').toUpperCase()}
                                        </div>
                                        <div style={{ fontSize: 13, color: dsVar('fg-body'), wordBreak: 'break-word', fontFamily: '"Inter", sans-serif' }}>
                                            {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const LegendLine: React.FC<{ color: string; dash: 'solid' | 'dotted' | 'dashed'; label: string }> = ({ color, dash, label }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 18, borderTop: `2px ${dash} ${color}` }} />
        {label}
    </span>
);

export default NetworkGraphVisualization;
